<template>
  <div class="wizzard__form">
    <TagItem
      v-model="assetClass"
      v-model:search="assetClassSearch"
      :label="ASSET_CLASS"
      :list="assetClassMappedList"
      focus-on-load
    />
    <TagItem
      v-model="investmentStrategy"
      v-model:search="investmentStrategySearch"
      :label="INVESTMENT_STRATEGY"
      :list="investmentStrategyMappedList"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, isRef, ref } from 'vue'
import { union } from 'lodash'

import { ASSET_CLASS, INVESTMENT_STRATEGY } from '../utils/const'
import { TAG_FIELD } from '@/const'

import { useTagsBunchStore } from '@/store/tags/bunch'

import TagItem from '../components/TagItem.vue'

type Props = {
  tags: Record<string, string>
}
const { tags } = defineProps<Props>()

defineOptions({ inheritAttrs: false })

type Emits = {
  'update:tags': [data: Record<string, string>]
}
const emit = defineEmits<Emits>()

const tagsBunchStore = useTagsBunchStore()

const assetClassSearch = ref(tags[ASSET_CLASS])
const investmentStrategySearch = ref(tags[INVESTMENT_STRATEGY])

const assetClassList = inject<string[]>('tagAssetClassList', [])
const investmentStrategyList = inject<string[] | ComputedRef<string[]>>(
  'tagInvestmentStrategyList',
  [],
)

const assetClass = computed({
  get() {
    return tags[ASSET_CLASS]
  },
  set(value) {
    emit('update:tags', { ...tags, [ASSET_CLASS]: value })
  },
})

const investmentStrategy = computed({
  get() {
    return tags[INVESTMENT_STRATEGY]
  },
  set(value) {
    emit('update:tags', { ...tags, [INVESTMENT_STRATEGY]: value })
  },
})

const commonTagsList = computed(() => {
  return Array.from(tagsBunchStore.getCommonList.values())
})

const assetClassTags = computed(() => commonTagsList.value
  .filter(item => item.field<string>(TAG_FIELD.TAG_NAME).value === ASSET_CLASS)
  .map(item => item.field<string>(TAG_FIELD.TAG_VALUE).value)
)

const investmentStrategyTags = computed(() => commonTagsList.value
  .filter(item => item.field<string>(TAG_FIELD.TAG_NAME).value === INVESTMENT_STRATEGY)
  .map(item => item.field<string>(TAG_FIELD.TAG_VALUE).value)
)

const assetClassMappedList = computed(() => {
  let list = assetClassList
  if (!list.includes(assetClassSearch.value) && !!assetClassSearch.value) {
    list = union(assetClassTags.value, list)
  }
  return list.map(value => ({ value, label: value }))
})

const investmentStrategyMappedList = computed(() => {
  let list = isRef(investmentStrategyList) ? investmentStrategyList.value : investmentStrategyList
  if (!list.includes(investmentStrategySearch.value) && !!investmentStrategySearch.value) {
    list = union(investmentStrategyTags.value, list)
  }
  return list.map(value => ({ value, label: value }))
})
</script>
